import { useState, useEffect } from 'react';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { Flex, Autocomplete, TextField, Icon, Spinner } from '@scale/scaleui-radix';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { useLookupSquadsById } from 'react-scale/components/WebinarSessions/api';
import { IListItem } from 'react-scale/components/WebinarSessions/types';
import { useQuery } from '@tanstack/react-query-v4';
import EnrichedError from 'react-scale/lib/enrichedError';
import apiFetch from '@scale/frontend-lib/api-fetch';
import _ from 'lodash';

interface SquadAutocompleteProps {
  squadIds: string[];
  onSquadIdsChange: (squadIds: string[]) => void;
}

const useLookupSquadAutocomplete = ({ searchText }: { searchText: string }) => {
  return useQuery<IListItem[]>({
    queryKey: ['useLookupSquadAutocomplete', searchText],
    queryFn: async () => {
      const url = `corp-api/find/squad?name=${searchText}`;
      try {
        if (!searchText) return Promise.resolve([]);
        const { squads } = await apiFetch<{ squads: IListItem[] }>(null, url);
        return squads;
      } catch (error) {
        throw new EnrichedError(
          error.status,
          `Couldn't search for squads based on name.`,
          error.response?.message ?? error.toString(),
          `Error searching for squads based on name.`,
          url,
        );
      }
    },
  });
};

export const SquadAutocomplete = ({ squadIds, onSquadIdsChange }: SquadAutocompleteProps) => {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebouncedValue(searchInput, 500);
  const [fetchedSquads, setFetchedSquads] = useState<Record<string, string>>({});

  // Fetches the squads to show up as options based on the search input
  const { data: squadData, isFetching: isFetchingSquadData } = useLookupSquadAutocomplete({
    searchText: debouncedSearchInput,
  });
  const squadOptions = squadData?.map((squad: IListItem) => ({
    value: squad.id,
    label: squad.name,
  }));

  // Fetches the names for the currently selected squads
  const squadIdsToFetch = squadIds.filter(id => !fetchedSquads[id]);
  const { data: newSquads, isFetching: isFetchingNewSquads } = useLookupSquadsById({
    squadIds: squadIdsToFetch,
  });

  useEffect(() => {
    if (newSquads && Object.keys(newSquads).length > 0) {
      const squadNamesById: Record<string, string> = newSquads.reduce((acc, item: IListItem) => {
        acc[item.id] = item.name;
        return acc;
      }, {} as Record<string, string>);
      setFetchedSquads(prev => ({
        ...prev,
        ...squadNamesById,
      }));
    }
  }, [newSquads]);

  // TODO: Once Radix is migrated to 1.36, we can use the AsyncItems and AsyncValues to replace the loading state
  return (
    <Autocomplete.Root
      multiple
      search={searchInput}
      onSearchChange={setSearchInput}
      value={squadIds}
      onValueChange={(newSquadIds: string[]) => {
        if (!_.isEqual(newSquadIds, squadIds)) {
          onSquadIdsChange(newSquadIds);
        }
      }}
    >
      <Autocomplete.Content>
        <Autocomplete.TextField
          placeholder="Search by squad name..."
          // Do not allow invalid values from paste
          onPaste={() => {}}
        >
          <TextField.Slot side="left">
            <Icon icon={faSearch} label="Search" />
          </TextField.Slot>
          <Autocomplete.Values>
            {squadIds.map(squadId => (
              <Autocomplete.Value key={squadId} value={squadId}>
                {isFetchingNewSquads && !fetchedSquads[squadId] ? (
                  <Spinner />
                ) : (
                  fetchedSquads[squadId]
                )}
              </Autocomplete.Value>
            ))}
          </Autocomplete.Values>
          <Autocomplete.Reset />
        </Autocomplete.TextField>

        <Autocomplete.Items>
          {isFetchingSquadData
            ? [
                <Autocomplete.Item
                  key={'fetching-squads'}
                  keywords={searchInput}
                  value={searchInput}
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <Flex direction="row" gap="2">
                    <Spinner />
                    {'Fetching squads...'}
                  </Flex>
                </Autocomplete.Item>,
              ]
            : (squadOptions ?? []).map(squad => (
                <Autocomplete.Item key={squad.value} value={squad.value}>
                  {squad.label}
                </Autocomplete.Item>
              ))}
        </Autocomplete.Items>
      </Autocomplete.Content>
    </Autocomplete.Root>
  );
};
